import numeral from 'numeral'

/**
 * Formats the member portion based on its value and optional parameters.
 *
 * @param {number} memberPortion - The member portion value to be formatted.
 * @param {boolean} [hidePrefix] - Whether to hide the prefix/postfix.
 * @returns {string} The formatted member portion string.
 *
 * @description
 * The function takes a member portion value as input and formats it based on the following rules:
 * - If the member portion is less than -1, it rounds up using `Math.ceil()`.
 * - If the member portion is greater than or equal to -1, it uses the original value.
 * - If the member portion is between -1 and 1, no rounding is done.
 * - If hidePrefix is true, it only returns the formatted number.
 *
 * @example
 * formatMemberPortion({memberPortion: 10, hidePrefix: false})       // returns '$10 over'
 * formatMemberPortion({memberPortion: -5, hidePrefix: false})       // returns '$5 savings'
 * formatMemberPortion({memberPortion: 10, hidePrefix: true})        // returns '$10'
 */
export const formatMemberPortion = ({
  memberPortion,
  hidePrefix = false,
}: {
  memberPortion: number
  hidePrefix?: boolean
}): string => {
  const roundedUpIfBelowNegativeOne =
    memberPortion < -1 ? Math.ceil(memberPortion) : memberPortion
  const formattedAmount = numeral(Math.abs(memberPortion).toFixed(2)).format(
    '$0,0.00',
  )

  if (hidePrefix) {
    return formattedAmount
  } else {
    const postfix = roundedUpIfBelowNegativeOne <= 0 ? 'savings' : 'over'
    return `${formattedAmount} ${postfix}`
  }
}
